/**
 * Sale Form component for creating sale transactions
 * Using React Hook Form with Zod validation
 */

import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import * as z from 'zod'
import { useAssets } from '@/services/api/assets'
import { useCreateSale } from '@/services/api/sales'
import { localToUtc, getTodayDateInput } from '@/utils/dateUtils'
import { formatVND } from '@/utils/currencyUtils'
import { Input } from '@/components/ui/input'
import { CurrencyInput } from '@/components/ui/currency-input'
import { DatePicker } from '@/components/common/DatePicker'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { toast } from 'sonner'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Card, CardContent } from '@/components/ui/card'

interface SaleFormProps {
  onSuccess?: () => void
  onCancel?: () => void
  /**
   * Optional default assetId to pre-select (e.g., when opened from Asset card)
   */
  defaultAssetId?: string
}

export function SaleForm({ onSuccess, onCancel, defaultAssetId }: SaleFormProps) {
  const { data: assets = [] } = useAssets('holding') // Only show holding assets
  const createMutation = useCreateSale()

  const saleFormSchema = z.object({
    assetId: z.string().min(1, 'Vui lòng chọn tài sản'),
    saleDate: z.string().min(1, 'Ngày bán là bắt buộc'),
    salePrice: z.number().min(1, 'Giá bán phải lớn hơn 0'),
    quantitySold: z.number().min(0.01, 'Số lượng bán phải lớn hơn 0'),
    fees: z.number().min(0, 'Phí không được âm'),
  }).refine(
    (data) => {
      const revenue = data.salePrice * data.quantitySold - data.fees
      return revenue >= 0
    },
    {
      message: 'Phí không được lớn hơn doanh thu (giá bán × số lượng)',
      path: ['fees'],
    }
  )

  type SaleFormValues = z.infer<typeof saleFormSchema>

  const form = useForm<SaleFormValues>({
    resolver: zodResolver(saleFormSchema),
    defaultValues: {
      assetId: defaultAssetId || '',
      saleDate: getTodayDateInput(),
      salePrice: 0,
      quantitySold: 0,
      fees: 0,
    },
  })

  const selectedAssetId = form.watch('assetId')
  const selectedAsset = assets.find((a) => a.id === selectedAssetId) || null
  const salePrice = form.watch('salePrice')
  const quantitySold = form.watch('quantitySold')
  const fees = form.watch('fees')
  const revenue = salePrice * quantitySold - fees

  // If defaultAssetId changes (e.g., opening dialog from asset card), set it
  useEffect(() => {
    if (defaultAssetId) {
      form.setValue('assetId', defaultAssetId, { shouldValidate: true })
    }
  }, [defaultAssetId, form])

  // Validate and adjust quantity when asset changes
  useEffect(() => {
    if (selectedAsset && quantitySold > selectedAsset.quantity) {
      form.setValue('quantitySold', selectedAsset.quantity, { shouldValidate: true })
    }
  }, [selectedAsset, quantitySold, form])

  // Custom validation for quantitySold
  useEffect(() => {
    if (selectedAsset && quantitySold > selectedAsset.quantity) {
      form.setError('quantitySold', {
        type: 'manual',
        message: `Số lượng bán không được vượt quá số lượng còn lại (${selectedAsset.quantity} lượng)`,
      })
    } else {
      form.clearErrors('quantitySold')
    }
  }, [selectedAsset, quantitySold, form])

  const onSubmit = async (data: SaleFormValues) => {
    try {
      // Get current form values to ensure we have the latest state
      const currentQuantitySold = form.getValues('quantitySold')
      const currentSalePrice = form.getValues('salePrice')
      
      // Ensure all numeric values are properly converted
      const quantitySold = Number(currentQuantitySold || data.quantitySold)
      const salePrice = Number(currentSalePrice || data.salePrice)
      const fees = Number(data.fees || 0)

      // Validate before submitting
      if (isNaN(quantitySold) || quantitySold <= 0) {
        form.setError('quantitySold', { 
          type: 'manual', 
          message: 'Số lượng bán phải lớn hơn 0' 
        })
        return
      }

      if (isNaN(salePrice) || salePrice <= 0) {
        form.setError('salePrice', { 
          type: 'manual', 
          message: 'Giá bán phải lớn hơn 0' 
        })
        return
      }

      if (selectedAsset && quantitySold > selectedAsset.quantity + 0.001) {
        form.setError('quantitySold', {
          type: 'manual',
          message: `Số lượng bán không được vượt quá số lượng còn lại (${selectedAsset.quantity} lượng)`,
        })
        return
      }

      const saleDateUtc = localToUtc(new Date(data.saleDate))
      await createMutation.mutateAsync({
        assetId: data.assetId,
        saleDate: saleDateUtc,
        salePrice: salePrice,
        quantitySold: quantitySold,
        fees: fees,
      })

      onSuccess?.()
    } catch (error) {
      console.error('Failed to create sale:', error)
      const errorMessage = error instanceof Error ? error.message : 'Không thể tạo giao dịch bán. Vui lòng thử lại.'
      toast.error(errorMessage)
      form.setError('root', { message: errorMessage })
    }
  }

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      {form.formState.errors.root && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-destructive text-sm">{form.formState.errors.root.message}</p>
          </CardContent>
        </Card>
      )}

      <FieldGroup>
        <Controller
          name="assetId"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="sale-form-assetId">
                Tài sản <span className="text-destructive">*</span>
              </FieldLabel>
              <Select
                {...(field.value ? { value: field.value } : {})}
                onValueChange={field.onChange}
              >
                <SelectTrigger id="sale-form-assetId" aria-invalid={fieldState.invalid}>
                  <SelectValue placeholder="Chọn tài sản" />
                </SelectTrigger>
                <SelectContent>
                  {assets.map((asset) => (
                    <SelectItem key={asset.id} value={asset.id}>
                      {asset.name} - {asset.goldTypeName} ({asset.quantity} lượng còn lại)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedAsset && (
                <FieldDescription>
                  Số lượng còn lại: <strong>{selectedAsset.quantity} lượng</strong>
                </FieldDescription>
              )}
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="saleDate"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="sale-form-saleDate">
                Ngày bán <span className="text-destructive">*</span>
              </FieldLabel>
              <DatePicker
                value={field.value}
                onChange={field.onChange}
                id="sale-form-saleDate"
                aria-invalid={fieldState.invalid}
                placeholder="Chọn ngày bán"
              />
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="salePrice"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="sale-form-salePrice">
                Giá bán (VND) <span className="text-destructive">*</span>
              </FieldLabel>
              <CurrencyInput
                id="sale-form-salePrice"
                name={field.name}
                value={field.value?.toString() || ''}
                onValueChange={(value) => {
                  const numValue = value ? parseFloat(value) : undefined
                  field.onChange(numValue)
                }}
                onBlur={field.onBlur}
                aria-invalid={fieldState.invalid}
                placeholder="Ví dụ: 75,000,000"
              />
              <FieldDescription>
                Nhập giá bán mỗi lượng vàng (đơn vị: VND). Ví dụ: 75,000,000
              </FieldDescription>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="quantitySold"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="sale-form-quantitySold">
                Số lượng bán (lượng) <span className="text-destructive">*</span>
              </FieldLabel>
              <div className="flex items-center gap-2">
              <Input
                {...field}
                id="sale-form-quantitySold"
                type="number"
                step="0.01"
                min="0.01"
                max={selectedAsset?.quantity || 1000}
                value={field.value > 0 ? field.value : ''}
                onChange={(e) => {
                  const value = e.target.value
                  if (value === '') {
                    field.onChange(0)
                  } else {
                    const numValue = parseFloat(value)
                    field.onChange(isNaN(numValue) ? 0 : numValue)
                  }
                }}
                onFocus={(e) => {
                  if (field.value === 0 || !field.value) {
                    setTimeout(() => e.target.select(), 0)
                  }
                }}
                aria-invalid={fieldState.invalid}
                placeholder="0"
              />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-h-[36px] shrink-0"
                  disabled={!selectedAsset || !selectedAsset.quantity || selectedAsset.quantity <= 0}
                  onClick={async () => {
                    if (selectedAsset && selectedAsset.quantity > 0) {
                      // Set the value and trigger validation
                      await form.setValue('quantitySold', selectedAsset.quantity, { 
                        shouldValidate: true,
                        shouldDirty: true,
                        shouldTouch: true
                      })
                      // Wait a bit then trigger validation to ensure it runs
                      setTimeout(() => {
                        form.trigger('quantitySold')
                      }, 0)
                    }
                  }}
                >
                  Tất cả
                </Button>
              </div>
              <FieldDescription>
                Số lượng vàng bán (tối đa: {selectedAsset?.quantity || 'N/A'} lượng)
              </FieldDescription>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />

        <Controller
          name="fees"
          control={form.control}
          render={({ field, fieldState }) => (
            <Field data-invalid={fieldState.invalid}>
              <FieldLabel htmlFor="sale-form-fees">
                Phí (VND)
              </FieldLabel>
              <CurrencyInput
                id="sale-form-fees"
                name={field.name}
                value={field.value?.toString() || ''}
                onValueChange={(value) => {
                  const numValue = value ? parseFloat(value) : undefined
                  field.onChange(numValue)
                }}
                onBlur={field.onBlur}
                aria-invalid={fieldState.invalid}
                placeholder="Ví dụ: 100,000"
              />
              <FieldDescription>
                Phí giao dịch (nếu có). Ví dụ: 100,000
              </FieldDescription>
              {fieldState.invalid && <FieldError errors={[fieldState.error]} />}
            </Field>
          )}
        />
      </FieldGroup>

      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <div className="flex justify-between items-center gap-2 min-w-0">
            <span className="text-sm font-medium text-foreground shrink-0">Doanh thu (tự động tính):</span>
            <span className="text-lg font-bold text-primary break-words overflow-hidden text-right">{formatVND(revenue)}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            = (Giá bán × Số lượng) - Phí
          </p>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {createMutation.isPending && (
          <Progress value={100} className="w-full" />
        )}
        <div className="flex gap-2 justify-end">
          {onCancel && (
            <Button type="button" onClick={onCancel} variant="outline" className="min-h-[44px]" disabled={createMutation.isPending}>
              Hủy
            </Button>
          )}
          <Button
            type="submit"
            disabled={createMutation.isPending}
            className="min-h-[44px]"
          >
            {createMutation.isPending ? 'Đang tạo...' : 'Tạo giao dịch bán'}
          </Button>
        </div>
      </div>
    </form>
  )
}
